<template>
  <WdsStack direction-column gap-md class="tw-w-full" data-test="client-people-tab">
    <WdsStack
      v-if="hasSwitcher"
      direction-row
      align-center
      justify-space-between
      wrap
      gap-md
      class="tw-w-full"
    >
      <WdsSegmentedControl
        :default-active-key="active"
        data-test="client-people-segments"
        @click="select"
      >
        <WdsSegmentedControlButton
          v-for="option in segments"
          :key="option.key"
          :option-key="option.key"
          :data-test="`client-people-segment-${option.key}`"
        >
          <WdsStack align-center gap-sm class="tw-px-2">
            <WdsText :text="option.title" />
            <WdsLabel xs :data-test="`client-people-count-${option.key}`">
              {{ option.count ?? '—' }}
            </WdsLabel>
          </WdsStack>
        </WdsSegmentedControlButton>
      </WdsSegmentedControl>

      <WdsButton
        v-if="showsManageTeam"
        primary
        data-test="client-people-manage-team"
        @click="managing = true"
      >
        {{ $t('Manage team') }}
      </WdsButton>
    </WdsStack>

    <!-- Without a switcher the button joins the helper line, so it stays on the right. -->
    <WdsStack direction-row align-center justify-space-between wrap gap-md class="tw-w-full">
      <WdsText data-test="client-people-helper">
        <template v-if="active === PeopleSegment.Team">
          {{ $t('Client owners from your company for :client.', { client: clientName ?? '' }) }}
        </template>
        <template v-else-if="active === PeopleSegment.Agencies && isAgencyLink">
          {{ $t('People the MSP has assigned to :client.', { client: clientName ?? '' }) }}
        </template>
        <template v-else-if="active === PeopleSegment.Agencies">
          {{
            $t(
              'Recruiters that linked staffing agencies have assigned. Each agency manages its own recruiters.',
            )
          }}
        </template>
        <template v-else>
          {{ $t('Everyone at :client with a login to their account.', { client: clientName ?? '' }) }}
        </template>
      </WdsText>

      <WdsButton
        v-if="!hasSwitcher && showsManageTeam"
        primary
        data-test="client-people-manage-team"
        @click="managing = true"
      >
        {{ $t('Manage team') }}
      </WdsButton>
    </WdsStack>

    <ClientTeamOwnersTable
      v-if="active === PeopleSegment.Team"
      :client-relation-id="clientRelationId"
      :can-manage="canManageOwners"
      :assigned-total="teamTotal"
      @manage="managing = true"
    />
    <ClientAgencyRecruitersTable
      v-else-if="active === PeopleSegment.Agencies"
      :client-relation-id="clientRelationId"
      :is-agency-link="isAgencyLink"
      :recruiter-total="recruiterTotal"
      :linked-agencies="linkedAgencyTotal"
      :client-name="clientName"
      @open-agencies="emit('openAgencies')"
    />
    <ClientTeamMembersTable v-else :client-relation-id="clientRelationId" />

    <ClientOwnersModal
      v-if="managing && canManageOwners"
      :show="managing"
      :client-relation-id="clientRelationId"
      @update:show="managing = $event"
    />
  </WdsStack>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsButton,
  WdsLabel,
  WdsSegmentedControl,
  WdsSegmentedControlButton,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { ClientPeopleCountsQuery } from '@/components/Clients/graphql/clientPeopleCountsQuery'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import ClientTeamOwnersTable from '@/components/Clients/People/ClientTeamOwnersTable.vue'
import ClientAgencyRecruitersTable from '@/components/Clients/People/ClientAgencyRecruitersTable.vue'
import ClientTeamMembersTable from '@/components/Clients/People/ClientTeamMembersTable.vue'
import ClientOwnersModal from '@/components/Owners/ClientOwnersModal.vue'
import { PeopleSegment } from '@/components/Clients/clientPageTabs'

const props = withDefaults(
  defineProps<{
    clientRelationId: string
    clientName: string | null
    canManageOwners: boolean
    canManageAgencies: boolean
    /** A staffing agency's own link rather than a supplier company's relation. */
    isAgencyLink?: boolean
    /** Agency links only; null until the link has loaded. */
    isDirect?: boolean | null
  }>(),
  { isAgencyLink: false, isDirect: null },
)

// The page owns the URL key so other surfaces can open a segment.
const segment = defineModel<string | undefined>('segment')

const emit = defineEmits<{ (e: 'openAgencies'): void }>()

// Until an agency link loads, its kind is unknown and only your team shows.
const showsCounterpart = computed(() =>
  props.isAgencyLink ? props.isDirect === false : props.canManageAgencies,
)

// Agency links have no client team, and only a managed link has an MSP on the other side.
const active = computed(() => {
  if (segment.value === PeopleSegment.Agencies && showsCounterpart.value) {
    return PeopleSegment.Agencies
  }
  if (segment.value === PeopleSegment.Client && !props.isAgencyLink) {
    return PeopleSegment.Client
  }

  return PeopleSegment.Team
})

const select = (key: string) => {
  segment.value = key
}

// Scoped exactly like the tables, which send the current account, so a badge and its table agree.
const { result: counts } = useQuery(
  ClientPeopleCountsQuery,
  () => ({
    client: props.clientRelationId,
    accounts: [currentAccount.globalId],
    companyRelation: !props.isAgencyLink,
  }),
  { clientId: 'publicApi' },
)

const teamTotal = computed(() => counts.value?.team?.paginatorInfo.total ?? null)
const recruiterTotal = computed(() => counts.value?.recruiters?.paginatorInfo.total ?? null)
const linkedAgencyTotal = computed(() =>
  counts.value?.relation?.__typename === 'CompanySupplier'
    ? counts.value.relation.linkedStaffingAgencies.paginatorInfo.total
    : null,
)

const segments = computed(() =>
  [
    { key: PeopleSegment.Team, title: $t('Your team'), count: teamTotal.value },
    {
      key: PeopleSegment.Agencies,
      title: props.isAgencyLink ? $t('MSP team') : $t('Agency recruiters'),
      count: recruiterTotal.value,
      hidden: !showsCounterpart.value,
    },
    {
      key: PeopleSegment.Client,
      title: $t('Client team'),
      count: counts.value?.clientTeam?.paginatorInfo.total ?? null,
      hidden: props.isAgencyLink,
    },
  ].filter((option) => !option.hidden),
)

const hasSwitcher = computed(() => segments.value.length > 1)
const showsManageTeam = computed(() => active.value === PeopleSegment.Team && props.canManageOwners)

const managing = ref(false)
</script>
